import { parse as parseQuery } from 'qs';

import constants from '../constants.json';
import { localStorage } from './storage';

let API_BASE: string;
if (process.env.VITE_API_BASE) {
  API_BASE = process.env.VITE_API_BASE;
} else {
  API_BASE = constants.PORTAL_API_BASE_URL;
}
let SUBSCRIPTIONS_API_BASE: string;
if (process.env.VITE_SUBSCRIPTIONS_API_BASE) {
  SUBSCRIPTIONS_API_BASE = process.env.VITE_SUBSCRIPTIONS_API_BASE;
} else {
  SUBSCRIPTIONS_API_BASE =
    (constants as typeof constants & { PORTAL_SUBSCRIPTIONS_API_BASE_URL?: string })
      .PORTAL_SUBSCRIPTIONS_API_BASE_URL ?? API_BASE;
}
let IDENTITY_API_BASE;
if (process.env.VITE_PORTAL_IDENTITY_BASE_URL) {
  IDENTITY_API_BASE = process.env.VITE_PORTAL_IDENTITY_BASE_URL;
} else {
  // Default to the same as API_BASE
  IDENTITY_API_BASE = API_BASE;
}

export { API_BASE };

/**
 * Hosts the fd-api / fd-subscriptions-api / fd-identity-api runtime override query params
 * are allowed to point at, matched exactly or as a dot-anchored suffix (so
 * `evil-portal.flipdish.com.attacker.net` cannot pass as `portal.flipdish.com`). Mirrors the
 * allowlist approach in @flipdish/ui-connected's UIConnectedProvider.
 */
const ALLOWED_API_OVERRIDE_HOSTS = [
  'portal.flipdish.com', // production portal + API (api.portal.flipdish.com)
  'portal.flipdishdev.com', // team/dev environments (int-team, orders-team, subscriptions-integration, ...)
  'flipdish.dev', // internal dev domain (api.portal.flipdish.dev, identity.flipdish.dev)
];

const isLoopbackHostname = (hostname: string) =>
  hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';

function isAllowedApiOverrideHost(rawUrl: unknown): rawUrl is string {
  if (typeof rawUrl !== 'string') {
    return false;
  }

  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return false;
  }

  const loopback = isLoopbackHostname(url.hostname);

  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && loopback)) {
    return false;
  }

  return (
    loopback ||
    ALLOWED_API_OVERRIDE_HOSTS.some(
      (host) => url.hostname === host || url.hostname.endsWith(`.${host}`)
    )
  );
}

// Applies a `?<queryKey>=<url>` override to localStorage[storageKey], rejecting hosts that
// aren't on the allowlist instead of persisting them.
function applyAllowedApiOverride(storageKey: string, queryKey: string): void {
  const query = parseQuery(window.location.search.substring(1));

  if (!(queryKey in query)) {
    return;
  }

  const override = query[queryKey];
  if (!override) {
    localStorage.removeItem(storageKey);
  } else if (isAllowedApiOverrideHost(override)) {
    localStorage.setItem(storageKey, override);
  } else if (process.env.NODE_ENV === 'development') {
    console.error(
      `[apibase] Ignoring ${queryKey} override: host is not on the allowlist`,
      override
    );
  }
}

// Reads localStorage[storageKey], treating any value that isn't on the allowlist as
// untrusted (e.g. persisted by a version of this code before the allowlist existed) and
// clearing it rather than returning it.
function readAllowedApiOverride(storageKey: string): string | null {
  const stored = localStorage.getItem(storageKey);
  if (stored && !isAllowedApiOverrideHost(stored)) {
    localStorage.removeItem(storageKey);
    return null;
  }
  return stored;
}

export function getApiEndPoint() {
  applyAllowedApiOverride('flipdish-api-endpoint', 'fd-api');
  return readAllowedApiOverride('flipdish-api-endpoint') || API_BASE;
}

// TODO: Replace URL with dynamic url once ready
export const getSubscriptionsApiEndPoint = () => {
  applyAllowedApiOverride('flipdish-subscriptions-api-endpoint', 'fd-subscriptions-api');
  return readAllowedApiOverride('flipdish-subscriptions-api-endpoint') || SUBSCRIPTIONS_API_BASE;
};

export const getIdentityApiEndpoint = () => {
  return readAllowedApiOverride('identity-api-endpoint') ?? IDENTITY_API_BASE;
};

export const setIdentifyApiEndpoint = () => {
  applyAllowedApiOverride('identity-api-endpoint', 'fd-identity-api');
};

// #region @flipdish/api-client-typescript response Date FIXES

export function tryFixApiWrongDateParsingPaginationResult<R>(
  responseObj: { body: Flipdish.RestApiPaginationResult<any> },
  dateProps: Array<keyof R>,
  defaultValue: Flipdish.RestApiPaginationResult<R>
): Flipdish.RestApiPaginationResult<R> {
  try {
    const { Data, Limit, Page, TotalRecordCount } = responseObj.body;

    const data = Data.map((obj) => {
      const result = {} as R;
      const overrides = dateProps.reduce(
        (agg, prop) => {
          if (obj[prop] && typeof obj[prop] === 'string') {
            // TODO: check if contains zone offset
            const utcDate = new Date(
              (obj[prop] as string).endsWith('Z') ? obj[prop] : `${obj[prop]}Z`
            );
            agg[prop] = utcDate;
          }
          return agg;
        },
        {} as { [key in keyof R]: Date }
      );

      // @ts-expect-error - not touching this
      Object.assign(result, obj, overrides);
      return result;
    });

    return {
      Limit,
      Page,
      TotalRecordCount,
      Data: data,
    } as Flipdish.RestApiPaginationResult<R>;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(error);
    }
    return defaultValue;
  }
}
export function tryFixApiWrongDateParsing<R>(
  responseObj: { body: any },
  dateProps: Array<keyof R>,
  defaultValue: R
): R {
  try {
    let rawData = responseObj.body;
    if (rawData.Data) {
      rawData = rawData.Data;
    }

    const result = {} as R;

    const overrides = dateProps.reduce(
      (agg, prop) => {
        if (rawData[prop] && typeof rawData[prop] === 'string') {
          // TODO: check if contains zone offset
          const utcDate = new Date(
            (rawData[prop] as string).endsWith('Z') ? rawData[prop] : `${rawData[prop]}Z`
          );
          agg[prop] = utcDate;
        }
        return agg;
      },
      {} as { [key in keyof R]: Date }
    );

    // @ts-expect-error - not touching this
    Object.assign(result, rawData, overrides);

    return result;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(error);
    }
    return defaultValue;
  }
}

// #endregion
